import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { usePageHeader } from '#src/providers/page-layout-provider';

export interface PageHeaderPortalProps {
  /** When set (including `null`), syncs to the shell header title; omit to leave title unchanged. */
  title?: string | ReactNode | null;
  children?: ReactNode;
}

/**
 * Declarative bridge to {@link usePageHeader}: optional `title` for the sticky header title slot,
 * optional `children` portaled into the header actions container.
 */
export function PageHeaderPortal({ title, children }: PageHeaderPortalProps) {
  const { setTitle, actionsRef } = usePageHeader();

  useEffect(() => {
    if (title === undefined) return;
    setTitle(title);
    return () => setTitle(null);
  }, [setTitle, title]);

  if (children == null || !actionsRef.current) {
    return null;
  }

  return createPortal(children, actionsRef.current);
}
